import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { GanttChartSquare, Sparkles, Download, Users, ArrowUpRight, Menu, X } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import usePageMeta from "@/hooks/usePageMeta";
import useHideOnScrollDown from "@/hooks/useHideOnScrollDown";
import { safeHtml } from "@/lib/sanitize";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import { FEATURED_GUIDES } from "@/seo/featuredGuides.js";
import useCases from "@/data/useCases";
import { tradePagePath } from "@/seo/useCases.js";
import { openCookieSettings } from "@/lib/consent";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import BrandLogo from "@/components/BrandLogo";

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // Le blog est rédigé en français : les liens vers ses guides ne s'affichent que sur la landing française.
  const showGuides = (i18n.resolvedLanguage || i18n.language || "fr").startsWith("fr");
  const headerVisible = useHideOnScrollDown();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  usePageMeta({
    title: t("seo.landingTitle"),
    description: t("seo.landingDesc"),
    canonical: "https://flowgantt.eu/",
    hreflangBase: "https://flowgantt.eu/",
  });

  const features = [
    { icon: Sparkles, title: t("landing.feat1Title"), desc: t("landing.feat1Desc") },
    { icon: GanttChartSquare, title: t("landing.feat2Title"), desc: t("landing.feat2Desc") },
    { icon: Download, title: t("landing.feat3Title"), desc: t("landing.feat3Desc") },
    { icon: Users, title: t("landing.feat4Title"), desc: t("landing.feat4Desc") },
  ];

  const steps = [
    { n: "01", title: t("landing.step1Title"), desc: t("landing.step1Desc") },
    { n: "02", title: t("landing.step2Title"), desc: t("landing.step2Desc") },
    { n: "03", title: t("landing.step3Title"), desc: t("landing.step3Desc") },
  ];

  return (
    <div className="min-h-screen bg-white text-neutral-950 font-mono-plex overflow-x-hidden">
      {/* NAV — fixed with auto-hide on scroll-down, re-appears on scroll-up (all breakpoints) */}
      <header
        data-testid="landing-header"
        className={`fixed top-0 left-0 right-0 z-40 border-b border-stone-200 bg-white/90 backdrop-blur-xl pt-safe transition-transform duration-300 will-change-transform ${
          headerVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between gap-2">
          <Link
            to="/"
            data-testid="brand-link"
            className="flex items-center gap-2 shrink-0"
          >
            <BrandLogo className="w-7 h-7" />
            <span className="font-display font-black text-lg tracking-tighter">
              FLOWGANTT
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="hover:text-[#E05236] transition-colors">{t("nav.features")}</a>
            <a href="#how" className="hover:text-[#E05236] transition-colors">{t("nav.howItWorks")}</a>
            <a href="#pricing" className="hover:text-[#E05236] transition-colors">{t("nav.pricing")}</a>
          </nav>
          <div className="hidden md:flex items-center gap-2 sm:gap-3 shrink-0">
            <LanguageSwitcher />
            <Button
              variant="ghost"
              data-testid="nav-login-btn"
              onClick={() => navigate("/login")}
              className="rounded-sm text-sm"
            >
              {t("nav.login")}
            </Button>
            <Button
              data-testid="nav-signup-btn"
              onClick={() => navigate("/register")}
              className="rounded-sm bg-neutral-950 hover:bg-neutral-800 text-white text-sm px-4"
            >
              {t("nav.signup")}
            </Button>
          </div>

          {/* Mobile: burger menu */}
          <div className="flex md:hidden items-center gap-2">
            <LanguageSwitcher />
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  data-testid="mobile-menu-trigger"
                  aria-label={t("nav.menuOpen")}
                  className="w-10 h-10 grid place-items-center rounded-sm border border-stone-200 hover:bg-stone-50 transition-colors"
                >
                  <Menu className="w-5 h-5" strokeWidth={1.75} />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                data-testid="mobile-menu-panel"
                className="w-[86%] max-w-sm bg-white p-0 border-l border-stone-200 flex flex-col [&>button]:hidden"
              >
                <div className="flex items-center justify-between px-6 py-5 border-b border-stone-200">
                  <div className="flex items-center gap-2">
                    <BrandLogo className="w-7 h-7" />
                    <span className="font-display font-black text-lg tracking-tighter">
                      FLOWGANTT
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-label={t("nav.menuClose")}
                    data-testid="mobile-menu-close"
                    className="w-10 h-10 grid place-items-center rounded-sm hover:bg-stone-100"
                  >
                    <X className="w-5 h-5" strokeWidth={1.75} />
                  </button>
                </div>

                <nav className="flex-1 px-6 py-6 flex flex-col gap-1">
                  <a
                    href="#features"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-features"
                    className="py-4 border-b border-stone-100 font-display font-bold text-xl tracking-tight hover:text-[#E05236] transition-colors"
                  >
                    {t("nav.features")}
                  </a>
                  <a
                    href="#how"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-how"
                    className="py-4 border-b border-stone-100 font-display font-bold text-xl tracking-tight hover:text-[#E05236] transition-colors"
                  >
                    {t("nav.howItWorks")}
                  </a>
                  <a
                    href="#pricing"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-pricing"
                    className="py-4 border-b border-stone-100 font-display font-bold text-xl tracking-tight hover:text-[#E05236] transition-colors"
                  >
                    {t("nav.pricing")}
                  </a>
                  <a
                    href="#faq"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-faq"
                    className="py-4 border-b border-stone-100 font-display font-bold text-xl tracking-tight hover:text-[#E05236] transition-colors"
                  >
                    {t("landing.faqLabel")}
                  </a>
                </nav>

                <div className="px-6 pb-8 pt-4 border-t border-stone-200 flex flex-col gap-3">
                  <Button
                    data-testid="mobile-nav-login-btn"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/login");
                    }}
                    variant="outline"
                    className="w-full h-12 rounded-sm border-neutral-950 text-neutral-950 hover:bg-neutral-950 hover:text-white text-sm"
                  >
                    {t("nav.login")}
                  </Button>
                  <Button
                    data-testid="mobile-nav-signup-btn"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/register");
                    }}
                    className="w-full h-12 rounded-sm bg-neutral-950 hover:bg-neutral-800 text-white text-sm"
                  >
                    {t("nav.signup")}
                    <ArrowUpRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Spacer to offset the fixed header at every breakpoint */}
      <div className="h-16" aria-hidden />


      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-12 sm:pt-16 lg:pt-24 pb-16 sm:pb-20">
        <div className="grid grid-cols-12 gap-6 lg:gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.25em] font-semibold text-neutral-600 mb-6 sm:mb-8"
            >
              <span className="w-1.5 h-1.5 bg-[#E05236] rounded-full" />
              {t("landing.heroBadge")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tighter break-words">
              {t("landing.heroTitleA")}
              <br />
              <span className="text-[#E05236]">{t("landing.heroTitleB")}</span> {t("landing.heroTitleC")}
            </h1>
            <p className="mt-6 sm:mt-8 text-base md:text-lg text-neutral-600 max-w-xl leading-relaxed">
              {t("landing.heroSubtitle")}
            </p>
            <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
              <Button
                data-testid="hero-cta-primary"
                onClick={() => navigate("/register")}
                className="rounded-sm bg-neutral-950 hover:bg-neutral-800 text-white h-12 px-6 text-sm w-full sm:w-auto"
              >
                {t("landing.ctaPrimary")}
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                data-testid="hero-cta-secondary"
                onClick={() => navigate("/login")}
                className="rounded-sm border-neutral-950 h-12 px-6 text-sm w-full sm:w-auto"
              >
                {t("landing.ctaSecondary")}
              </Button>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col gap-4">
            <div className="border border-stone-200 p-6 rounded-sm bg-stone-50">
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">
                {t("landing.metricRealtime")}
              </div>
              <div className="font-display text-4xl font-black">48%</div>
              <div className="text-xs text-neutral-600 mt-1">
                {t("landing.metricTimeSaved")}
              </div>
            </div>
            <div className="border border-stone-200 p-6 rounded-sm">
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">
                {t("landing.metricActiveUsers")}
              </div>
              <div className="font-display text-4xl font-black">2 400+</div>
              <div className="text-xs text-neutral-600 mt-1">
                {t("landing.metricUsersTrust")}
              </div>
            </div>
            <div className="border border-neutral-950 bg-neutral-950 text-white p-6 rounded-sm">
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 mb-3">
                {t("landing.aiLabel")}
              </div>
              <div className="font-display text-2xl font-bold leading-tight">
                {t("landing.aiTagline")}
              </div>
            </div>
          </div>
        </div>

        {/* Mock Gantt preview */}
        <div className="mt-12 sm:mt-16 border border-stone-200 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[640px]">
              <div className="gantt-timeline-header flex items-center px-4 sm:px-6 py-3 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold">
                <div className="w-40 sm:w-48">{t("landing.mockColTask")}</div>
                <div className="flex-1 flex gap-8">
                  {[1,2,3,4,5,6].map(n => <span key={n}>{t("landing.mockWeek")} {n}</span>)}
                </div>
              </div>
              {[
                { name: t("landing.feat1Title"), left: 0, width: 22, color: "#E05236" },
                { name: t("landing.feat2Title"), left: 15, width: 30, color: "#4A5D4E" },
                { name: t("landing.feat3Title"), left: 25, width: 40, color: "#D4A373" },
                { name: t("landing.feat4Title"), left: 60, width: 25, color: "#7D8C96" },
                { name: t("landing.pricingTitle"), left: 88, width: 4, color: "#E05236" },
              ].map((tsk) => (
                <div key={tsk.name} className="gantt-row flex items-center px-4 sm:px-6 py-3">
                  <div className="w-40 sm:w-48 text-sm font-medium truncate pr-4">{tsk.name}</div>
                  <div className="flex-1 h-6 relative">
                    <div
                      className="absolute h-full rounded-sm gantt-bar"
                      style={{
                        left: `${tsk.left}%`,
                        width: `${tsk.width}%`,
                        background: tsk.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="bg-stone-50 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-32">
          <div className="max-w-2xl mb-12 sm:mb-16">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#E05236] font-semibold mb-4">
              {t("landing.featuresLabel")}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight break-words">
              {t("landing.featuresTitle")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div
                key={f.title}
                data-testid={`feature-${i}`}
                className="bg-white border border-stone-200 p-6 sm:p-8 rounded-sm hover:shadow-sm transition-shadow"
              >
                <f.icon className="w-6 h-6 text-neutral-950 mb-6" strokeWidth={1.5} />
                <div className="font-display font-bold text-lg mb-2">{f.title}</div>
                <p className="text-sm text-neutral-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-32">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-start">
          <div className="col-span-12 lg:col-span-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#E05236] font-semibold mb-4">
              {t("landing.howLabel")}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight break-words">
              {t("landing.howTitle")}
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-8 space-y-8">
            {steps.map((s, i) => (
              <div
                key={s.n}
                className="flex gap-4 sm:gap-8 border-b border-stone-200 pb-8 last:border-b-0"
                data-testid={`step-${i}`}
              >
                <div className="font-display font-black text-3xl text-[#E05236] w-12 sm:w-16 shrink-0">
                  {s.n}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-xl sm:text-2xl mb-2 break-words">
                    {s.title}
                  </div>
                  <p className="text-neutral-600 break-words">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="bg-stone-50 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-32">
          <div className="max-w-2xl mb-10 sm:mb-14">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#E05236] font-semibold mb-4">
              {t("landing.testimonialsLabel")}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter break-words">
              {t("landing.testimonialsTitle")}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                data-testid={`testimonial-${n}`}
                className="bg-white border border-stone-200 rounded-sm p-6 sm:p-8 flex flex-col"
              >
                <div className="text-[#E05236] font-display text-3xl leading-none mb-4">"</div>
                <p className="text-neutral-800 leading-relaxed text-base flex-1">
                  {t(`landing.testimonial${n}`)}
                </p>
                <div className="mt-6 pt-6 border-t border-stone-200">
                  <div className="font-display font-bold text-sm">
                    {t(`landing.testimonial${n}Author`)}
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    {t(`landing.testimonial${n}Role`)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-neutral-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-32">
          <div className="text-center mb-10 sm:mb-14">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#E05236] font-semibold mb-4">
              {t("nav.pricing")}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-black tracking-tighter break-words">
              {t("landing.pricingTitle")}
            </h2>
            <p className="mt-6 text-neutral-400 max-w-xl mx-auto">
              {t("landing.pricingSubtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Free */}
            <div
              data-testid="pricing-free"
              className="bg-neutral-900 border border-neutral-800 rounded-sm p-6 sm:p-8 flex flex-col"
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-semibold mb-4">
                {t("landing.tierFreeName")}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-5xl font-black">0€</span>
                <span className="text-neutral-500 text-sm">{t("landing.perMonth")}</span>
                <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">
                  {t("landing.priceSuffix")}
                </span>
              </div>
              <div className="text-xs text-neutral-500 mt-2">{t("landing.noCard")}</div>
              <ul className="mt-8 space-y-3 text-sm text-neutral-300 flex-1">
                {(t("landing.tierFreeFeatures", { returnObjects: true }) || []).map((f) => (
                  <li key={f} className={f.startsWith("✗") ? "text-neutral-500" : ""}>
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                data-testid="pricing-free-cta"
                onClick={() => navigate("/register")}
                variant="outline"
                className="mt-8 rounded-sm border-white/30 text-white hover:bg-white hover:text-neutral-950 h-12"
              >
                {t("landing.tierFreeCta")}
              </Button>
            </div>

            {/* Pro */}
            <div
              data-testid="pricing-pro"
              className="bg-white text-neutral-950 border border-[#E05236] rounded-sm p-6 sm:p-8 flex flex-col relative"
            >
              <div className="absolute -top-3 right-6 bg-[#E05236] text-white text-[10px] uppercase tracking-[0.25em] font-semibold px-3 py-1 rounded-sm">
                {t("landing.popular")}
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#E05236] font-semibold mb-4">
                {t("landing.tierProName")}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-5xl font-black">9€</span>
                <span className="text-neutral-500 text-sm">{t("landing.perMonth")}</span>
                <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">
                  {t("landing.priceSuffix")}
                </span>
              </div>
              <div
                className="text-xs text-neutral-500 mt-2"
                dangerouslySetInnerHTML={safeHtml(t("landing.tierProAnnual"))}
              />
              <ul className="mt-8 space-y-3 text-sm text-neutral-700 flex-1">
                {(t("landing.tierProFeatures", { returnObjects: true }) || []).map((f) => (
                  <li key={f} dangerouslySetInnerHTML={safeHtml(f)} />
                ))}
              </ul>
              <Button
                data-testid="pricing-pro-cta"
                onClick={() => navigate("/pricing")}
                className="mt-8 rounded-sm bg-[#E05236] hover:bg-[#C94A31] text-white h-12"
              >
                {t("landing.tierProCta")}
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Équipe */}
            <div
              data-testid="pricing-team"
              className="bg-neutral-900 border border-neutral-700 text-white rounded-sm p-6 sm:p-8 flex flex-col"
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 font-semibold mb-4">
                {t("landing.tierTeamName")}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-5xl font-black">29€</span>
                <span className="text-neutral-500 text-sm">{t("landing.perMonth")}</span>
                <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">
                  {t("landing.priceSuffix")}
                </span>
              </div>
              <div className="text-xs text-neutral-500 mt-2">
                {t("landing.tierTeamSeats")}
              </div>
              <ul className="mt-8 space-y-3 text-sm text-neutral-300 flex-1">
                {(t("landing.tierTeamFeatures", { returnObjects: true }) || []).map((f) => (
                  <li key={f} dangerouslySetInnerHTML={safeHtml(f)} />
                ))}
              </ul>
              <Button
                data-testid="pricing-team-cta"
                onClick={() => navigate("/team")}
                variant="outline"
                className="mt-8 rounded-sm border-white/30 text-white hover:bg-white hover:text-neutral-950 h-12"
              >
                {t("landing.tierTeamCta")}
              </Button>
            </div>
          </div>

          <div
            className="text-center mt-10 text-xs text-neutral-500"
            data-testid="pricing-payment-note"
          >
            {t("landing.paymentNote")}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-32">
          <div className="max-w-2xl mb-10 sm:mb-12">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#E05236] font-semibold mb-4">
              {t("landing.faqLabel")}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter break-words">
              {t("landing.faqTitle")}
            </h2>
          </div>
          <div className="divide-y divide-stone-200 border-y border-stone-200">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <details
                key={n}
                data-testid={`faq-item-${n}`}
                className="group py-5"
              >
                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                  <span className="font-display font-bold text-base sm:text-lg text-neutral-950 group-open:text-[#E05236] transition-colors break-words">
                    {t(`landing.faq${n}Q`)}
                  </span>
                  <span
                    className="shrink-0 mt-1 text-[#E05236] font-display font-black text-2xl leading-none transition-transform group-open:rotate-45"
                    aria-hidden
                  >
                    +
                  </span>
                </summary>
                <p className="mt-3 text-neutral-600 leading-relaxed text-sm max-w-3xl">
                  {t(`landing.faq${n}A`)}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {showGuides && (
        <section data-testid="landing-usecases" aria-labelledby="landing-usecases-title" className="border-t border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-20">
            <div className="max-w-2xl mb-8 sm:mb-10">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#E05236] font-semibold mb-4">Pour qui</div>
              <h2
                id="landing-usecases-title"
                className="font-display text-3xl sm:text-4xl font-black tracking-tighter break-words"
              >
                FlowGantt pour votre métier.
              </h2>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {useCases.map((u) => (
                <li key={u.slug}>
                  <Link
                    to={tradePagePath(u.slug)}
                    data-testid={`landing-usecase-${u.slug}`}
                    className="flex h-full flex-col justify-between gap-2 rounded-sm border border-stone-200 bg-white px-4 py-4 transition-colors hover:border-neutral-950"
                  >
                    <span className="flex items-center justify-between gap-3 text-sm font-semibold text-neutral-900">
                      {u.label}
                      <ArrowUpRight className="h-4 w-4 shrink-0 text-[#E05236]" aria-hidden />
                    </span>
                    <span className="text-xs leading-relaxed text-neutral-600">{u.description}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {showGuides && (
        <section
          data-testid="landing-guides"
          aria-labelledby="landing-guides-title"
          className="border-t border-stone-200 bg-stone-50/60"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-20">
            <div className="max-w-2xl mb-8 sm:mb-10">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#E05236] font-semibold mb-4">Guides</div>
              <h2
                id="landing-guides-title"
                className="font-display text-3xl sm:text-4xl font-black tracking-tighter break-words"
              >
                Apprendre à planifier un projet.
              </h2>
              <p className="mt-3 text-sm text-neutral-600">
                Méthodes, tutoriels et exemples chiffrés pour construire un diagramme de Gantt qui tient.
              </p>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {FEATURED_GUIDES.map((g) => (
                <li key={g.slug}>
                  <Link
                    to={`/blog/${g.slug}`}
                    data-testid={`landing-guide-${g.slug}`}
                    className="flex h-full items-center justify-between gap-3 rounded-sm border border-stone-200 bg-white px-4 py-3.5 text-sm font-semibold text-neutral-900 transition-colors hover:border-neutral-950"
                  >
                    <span>{g.label}</span>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-[#E05236]" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/blog"
              data-testid="landing-guides-all"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-900 underline underline-offset-4 hover:text-[#E05236]"
            >
              Tous les articles du blog
            </Link>
          </div>
        </section>
      )}

      <footer className="border-t border-stone-200 py-8 sm:py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-xs text-neutral-500">
            {t("landing.footer")}
          </div>
          <nav className="flex flex-wrap gap-6 text-xs text-neutral-500">
            <Link
              to="/blog"
              data-testid="footer-blog"
              className="hover:text-neutral-900 transition-colors"
            >
              Blog
            </Link>
            <Link
              to="/legal/mentions"
              data-testid="footer-mentions-legales"
              className="hover:text-neutral-900 transition-colors"
            >
              {t("footer.mentions")}
            </Link>
            <Link
              to="/legal/cgv"
              data-testid="footer-cgv"
              className="hover:text-neutral-900 transition-colors"
            >
              {t("footer.cgv")}
            </Link>
            <Link
              to="/legal/confidentialite"
              data-testid="footer-privacy"
              className="hover:text-neutral-900 transition-colors"
            >
              {t("footer.privacy")}
            </Link>
            <button
              type="button"
              onClick={openCookieSettings}
              data-testid="footer-cookies"
              className="hover:text-neutral-900 transition-colors"
            >
              {t("cookies.manage")}
            </button>
            <a
              href="mailto:contact@flowgantt.eu"
              data-testid="footer-contact"
              className="hover:text-neutral-900 transition-colors"
            >
              {t("footer.contact")}
            </a>
          </nav>
        </div>
      </footer>
      <StickyMobileCTA />
    </div>
  );
}
